import {
  type DropTargetDropEvent,
  type DropTargetOptions,
  Loading,
  Tooltip,
} from '@affine/component';
import { Guard, useGuard } from '@affine/core/components/guard';
import { useAppSettingHelper } from '@affine/core/components/hooks/affine/use-app-setting-helper';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { DocsService } from '@affine/core/modules/doc';
import { DocDisplayMetaService } from '@affine/core/modules/doc-display-meta';
import { DocsSearchService } from '@affine/core/modules/docs-search';
import { WorkspaceRealtimeService } from '@affine/core/modules/editor';
import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { GlobalContextService } from '@affine/core/modules/global-context';
import { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import {
  DocumentPresenceAvatarStack,
  WorkspacePresenceStore,
} from '@affine/core/modules/organize';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { recordWorkspaceProjectionDiagnostic } from '@affine/core/modules/workspace-projection-reconciliation/diagnostics';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { TemplateIcon } from '@blocksuite/icons/rc';
import {
  LiveData,
  MANUALLY_STOP,
  useLiveData,
  useService,
  useServices,
} from '@toeverything/infra';
import clsx from 'clsx';
import {
  type ComponentType,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { NEVER } from 'rxjs';

import {
  NavigationPanelTreeNode,
  type NavigationPanelTreeNodeDropEffect,
  type NavigationPanelTreeSelectionItem,
} from '../../tree';
import * as presenceStyles from '../presence-glow.css';
import type { GenericNavigationPanelNode } from '../types';
import { Empty } from './empty';
import { useNavigationPanelDocNodeOperations } from './operations';
import * as styles from './styles.css';

export const NavigationPanelDocNode = ({
  docId,
  onDrop,
  location,
  reorderable,
  isLinked,
  hideLinkedChildren,
  hideTrashOperation,
  disableDrag,
  canDrop,
  operations: additionalOperations,
  dropEffect,
  parentPath,
  icon: IconOverride,
}: {
  docId: string;
  isLinked?: boolean;
  hideLinkedChildren?: boolean;
  hideTrashOperation?: boolean;
  disableDrag?: boolean;
  forwardKey?: string;
  icon?: ComponentType<{ className?: string }>;
} & GenericNavigationPanelNode) => {
  const t = useI18n();
  const {
    docsSearchService,
    workspaceService,
    docsService,
    globalContextService,
    docDisplayMetaService,
    featureFlagService,
  } = useServices({
    WorkspaceService,
    DocsSearchService,
    DocsService,
    GlobalContextService,
    DocDisplayMetaService,
    FeatureFlagService,
  });
  const navigationPanelService = useService(NavigationPanelService);
  const presenceStore = useService(WorkspacePresenceStore);
  const { appSettings } = useAppSettingHelper();

  const active =
    useLiveData(globalContextService.globalContext.docId.$) === docId;
  const path = useMemo(
    () => [...(parentPath ?? []), `doc-${docId}`],
    [parentPath, docId]
  );
  const collapsed = useLiveData(navigationPanelService.collapsed$(path));
  const setCollapsed = useCallback(
    (value: boolean) => {
      navigationPanelService.setCollapsed(path, value);
    },
    [navigationPanelService, path]
  );
  const showLinkedChildren =
    appSettings.showLinkedDocInSidebar && !hideLinkedChildren;
  const isCollapsed = showLinkedChildren ? collapsed : true;

  const docRecord = useLiveData(docsService.list.doc$(docId));
  const canTrashDoc = useGuard('Doc_Trash', docId) === true;
  const DocIcon = useLiveData(
    docDisplayMetaService.icon$(docId, {
      reference: isLinked,
    })
  );
  const docTitle = useLiveData(docDisplayMetaService.title$(docId));
  const isInTrash = useLiveData(docRecord?.trash$);
  const isTemplate = useLiveData(
    docRecord?.properties$.selector(properties => properties.isTemplate)
  );
  useLayoutEffect(() => {
    recordWorkspaceProjectionDiagnostic({
      stage: 'sidebar:doc-node-layout',
      reason: docRecord ? 'record-ready' : 'record-missing',
      docIds: [docId],
    });
  }, [docId, docRecord]);
  const enableEmojiIcon = useLiveData(
    featureFlagService.flags.enable_emoji_doc_icon.$
  );

  // sl-jmyl.2: Presence glow + avatar data
  const glowingNodes = useLiveData(presenceStore.glowingNodeIds$);
  const docPresenceMap = useLiveData(presenceStore.docPresence$);
  const presenceUserIds = docPresenceMap.get(docId);

  // sl-jmyl.8: LIVE moderation state (includes own moderation via sessionState)
  const realtimeService = useService(WorkspaceRealtimeService);
  const activeModerations = useLiveData(realtimeService.activeModerations$);
  const sessionState = useLiveData(realtimeService.sessionState$);
  const isModerating = useLiveData(realtimeService.isModerating$);
  const realtimeConnected = useLiveData(realtimeService.isConnected$);
  const docModeration = activeModerations.find(m => m.docId === docId);
  const isDocGlowing = realtimeConnected && glowingNodes.has(docId);
  const isLive =
    realtimeConnected &&
    (!!docModeration || (isModerating && sessionState?.docId === docId));
  const moderatorUserId = docModeration?.moderatorUserId;
  const visiblePresenceUserIds = realtimeConnected ? presenceUserIds : null;

  const Icon = useCallback(
    ({ className }: { className?: string }) => {
      if (IconOverride) {
        return <IconOverride className={className} />;
      }
      if (isTemplate) {
        return <TemplateIcon className={className} />;
      }
      return <DocIcon className={className} />;
    },
    [DocIcon, IconOverride, isTemplate]
  );

  const children = useLiveData(
    useMemo(
      () =>
        LiveData.from<{ docId: string; title?: string; params?: any }[] | null>(
          !isCollapsed ? docsSearchService.watchRefsFrom(docId) : NEVER,
          null
        ),
      [docsSearchService, docId, isCollapsed]
    )
  );
  const searching = children === null;

  const [referencesLoading, setReferencesLoading] = useState(true);
  useLayoutEffect(() => {
    // Metadata-only sidebar rows must not materialize document content. Load
    // content only when linked children are enabled and this node is expanded.
    if (isCollapsed) {
      return;
    }
    const abortController = new AbortController();
    // sl-suoi.1.3: Ensure doc is in sync queue for selective sync
    workspaceService.workspace.engine.doc.addDoc(docId, 'linked-doc-expand');
    const undoSync = workspaceService.workspace.engine.doc.addPriority(
      docId,
      10
    );
    const undoIndexer = docsSearchService.indexer.addPriority(docId, 10);
    docsSearchService.indexer
      .waitForDocCompleted(docId, abortController.signal)
      .then(() => {
        setReferencesLoading(false);
      })
      .catch((err: unknown) => {
        if (err !== MANUALLY_STOP) {
          console.error(err);
        }
      });
    return () => {
      undoSync();
      undoIndexer();
      abortController.abort(MANUALLY_STOP);
    };
  }, [docId, docsSearchService, workspaceService, isCollapsed]);

  const dndData = useMemo(() => {
    if (disableDrag) {
      return undefined;
    }
    return {
      draggable: {
        entity: {
          type: 'doc',
          id: docId,
        },
        docTrash: {
          canTrashDoc,
        },
        from: location,
      },
      dropTarget: {
        at: 'navigation-panel:doc',
      },
    } satisfies AffineDNDData;
  }, [canTrashDoc, disableDrag, docId, location]);
  const selectionLocationAt = location?.at;
  const selectionLocationNodeId =
    location?.at === 'navigation-panel:organize:folder-node' ||
    location?.at === 'navigation-panel:organize:root-doc' ||
    location?.at === 'navigation-panel:private:folder-node' ||
    location?.at === 'navigation-panel:private:root-doc'
      ? location.nodeId
      : undefined;
  const selectionItem = useMemo<
    NavigationPanelTreeSelectionItem | undefined
  >(() => {
    if (
      selectionLocationAt !== 'navigation-panel:organize:folder-node' &&
      selectionLocationAt !== 'navigation-panel:organize:root-doc' &&
      selectionLocationAt !== 'navigation-panel:private:folder-node' &&
      selectionLocationAt !== 'navigation-panel:private:root-doc'
    ) {
      return undefined;
    }
    if (!selectionLocationNodeId) {
      return undefined;
    }
    return {
      entity: {
        type: 'doc',
        id: docId,
      },
      docTrash: {
        canTrashDoc,
      },
      from: {
        at: selectionLocationAt,
        nodeId: selectionLocationNodeId,
      },
      label: t.t(docTitle),
    };
  }, [
    canTrashDoc,
    docId,
    docTitle,
    selectionLocationAt,
    selectionLocationNodeId,
    t,
  ]);

  const handleRename = useAsyncCallback(
    async (newName: string) => {
      await docsService.changeDocTitle(docId, newName);
      track.$.navigationPanel.organize.renameOrganizeItem({ type: 'doc' });
    },
    [docId, docsService]
  );

  const handleDropOnDoc = useAsyncCallback(
    async (data: DropTargetDropEvent<AffineDNDData>) => {
      if (data.treeInstruction?.type === 'make-child') {
        // Doc-to-doc linking via drag is disabled — it confuses users
        return;
      } else {
        onDrop?.(data);
      }
    },
    [onDrop]
  );

  const handleDropEffectOnDoc = useCallback<NavigationPanelTreeNodeDropEffect>(
    data => {
      if (data.treeInstruction?.type === 'make-child') {
        // Doc-to-doc linking via drag is disabled
        return;
      } else {
        return dropEffect?.(data);
      }
    },
    [dropEffect]
  );

  const handleDropOnPlaceholder = useAsyncCallback(
    async (_data: DropTargetDropEvent<AffineDNDData>) => {
      // Doc-to-doc linking via drag is disabled — it confuses users
      return;
    },
    []
  );

  const handleCanDrop = useMemo<DropTargetOptions<AffineDNDData>['canDrop']>(
    () => args => {
      // Doc-to-doc linking via drag is disabled
      if (args.treeInstruction?.type === 'make-child') {
        return false;
      }
      return (typeof canDrop === 'function' ? canDrop(args) : canDrop) ?? true;
    },
    [canDrop]
  );

  const workspaceDialogService = useService(WorkspaceDialogService);
  const operations = useNavigationPanelDocNodeOperations(
    docId,
    useMemo(
      () => ({
        openInfoModal: () => workspaceDialogService.open('doc-info', { docId }),
        openNodeCollapsed: () => setCollapsed(false),
        hideTrashOperation,
      }),
      [docId, hideTrashOperation, setCollapsed, workspaceDialogService]
    )
  );

  const finalOperations = useMemo(() => {
    if (additionalOperations) {
      return [...operations, ...additionalOperations];
    }
    return operations;
  }, [additionalOperations, operations]);

  if (isInTrash || !docRecord) {
    return null;
  }

  return (
    <NavigationPanelTreeNode
      icon={Icon}
      iconClassName={
        clsx(
          isLive && presenceStyles.liveIconColor,
          isDocGlowing && presenceStyles.iconGlow
        ) || undefined
      }
      name={t.t(docTitle)}
      renameInputTestId={`rename-modal-input-${docId}`}
      className={
        isLive
          ? presenceStyles.liveGlow
          : isDocGlowing
            ? presenceStyles.presenceGlow
            : undefined
      }
      dndData={dndData}
      selectionItem={selectionItem}
      onDrop={handleDropOnDoc}
      renameable
      extractEmojiAsIcon={enableEmojiIcon}
      collapsed={isCollapsed}
      setCollapsed={setCollapsed}
      collapsible={showLinkedChildren}
      canDrop={handleCanDrop}
      to={`/${docId}`}
      onClick={() => {
        track.$.navigationPanel.docs.openDoc();
      }}
      active={active}
      postfix={
        <>
          {isLive && (
            <div className={presenceStyles.liveBadge}>
              LIVE
              <div className={presenceStyles.liveBadgeDot} />
            </div>
          )}
          {visiblePresenceUserIds && visiblePresenceUserIds.length > 0 && (
            <DocumentPresenceAvatarStack
              className={presenceStyles.presenceAvatarStack}
              userIds={visiblePresenceUserIds}
              moderatorUserId={moderatorUserId}
            />
          )}
          {referencesLoading && !isCollapsed && (
            <Tooltip
              content={t[
                'com.streamlain.rootAppSidebar.docs.references-loading'
              ]()}
            >
              <div className={styles.loadingIcon}>
                <Loading />
              </div>
            </Tooltip>
          )}
        </>
      }
      reorderable={reorderable}
      renameableGuard={{
        docId,
        action: 'Doc_Update',
      }}
      onRename={handleRename}
      childrenPlaceholder={
        searching ? null : (
          <Empty
            onDrop={handleDropOnPlaceholder}
            noAccessible={!!children && children.length > 0}
          />
        )
      }
      operations={finalOperations}
      revealOperations
      dropEffect={handleDropEffectOnDoc}
      data-testid={`navigation-panel-doc-${docId}`}
      data-mail-doc-drop-id={docId}
      explorerIconConfig={{
        where: 'doc',
        id: docId,
      }}
    >
      {showLinkedChildren ? (
        <Guard docId={docId} permission="Doc_Read">
          {(canRead: boolean | undefined) =>
            canRead
              ? children?.map((child: { docId: string }, index: number) => (
                  <NavigationPanelDocNode
                    key={`${child.docId}-${index}`}
                    docId={child.docId}
                    reorderable={false}
                    location={{
                      at: 'navigation-panel:doc:linked-docs',
                      docId,
                    }}
                    parentPath={path}
                    isLinked
                  />
                ))
              : null
          }
        </Guard>
      ) : null}
    </NavigationPanelTreeNode>
  );
};
